/**
 * ConfigureFlowShell.jsx
 * data-fit LLC — DecisionFlow
 * Stage 2 CONFIGURE — Configure flow parent shell
 * Branch: feature/compliance-lifecycle-foundation
 * Spec authority: ComponentSpec_CONFIGURE.md (S82) + MasterContext_S88_Close.md
 * Path: ui/src/components/tenant-admin/ConfigureFlowShell.jsx
 *
 * S89 audit patches applied:
 * - PATCH-22: Removed tenantContext bundle from all child invocations.
 * - Shell retains useAuth() import for future shell-level needs per spec pattern.
 * - OI-USEAUTH-PATH: ../../auth/AuthContext.jsx — unverified, remains open.
 * M040 C2: hardcoded colors → tokens, text-* → text-tok-*
 */

import React, { useCallback, useState } from 'react';
import { useAuth } from '../../auth/AuthContext.jsx';
import ReferenceDataReview from './ReferenceDataReview';
import FiscalPeriodActivation from './FiscalPeriodActivation';
import CompAdminUserCreation from './CompAdminUserCreation';
import DomainAssignment from './DomainAssignment';
import DomainCoverageSummary from './DomainCoverageSummary';
import ConfigureGateSummary from './ConfigureGateSummary';

export const API_BASE = '/api/v1';

export const FILE_SIZE_LIMIT_MB = '10';
export const ROW_LIMIT_PER_FILE = '10000';
export const AI_TIMEOUT_MS = '10000';

export function timeoutPromise(ms) {
  return new Promise((_, reject) => {
    window.setTimeout(() => {
      reject(new Error('AI_TIMEOUT'));
    }, Number(ms));
  });
}

const SCREEN_ORDER = {
  CONF_01: 'CONF_01',
  CONF_02: 'CONF_02',
  CONF_03: 'CONF_03',
  CONF_04: 'CONF_04',
  CONF_05: 'CONF_05',
  CONF_06: 'CONF_06'
};

export default function ConfigureFlowShell({
  tenantId,
  tier = 'essentials',
  importLogId = null,
  initialScreen = SCREEN_ORDER.CONF_01
}) {
  const [currentScreen, setCurrentScreen] = useState(initialScreen);
  const [aiStatus, setAiStatus] = useState('idle');
  const [aiResponse, setAiResponse] = useState(null);

  const navigateTo = useCallback((target, extra = {}) => {
    setCurrentScreen(target);
    window.dispatchEvent(
      new CustomEvent('decisionflow:navigate', {
        detail: { target, source: 'CONFIGURE', ...extra }
      })
    );
  }, []);

  const goToReferenceData   = useCallback(() => navigateTo(SCREEN_ORDER.CONF_01), [navigateTo]);
  const goToFiscalPeriods   = useCallback(() => navigateTo(SCREEN_ORDER.CONF_02), [navigateTo]);
  const goToCompAdmins      = useCallback(() => navigateTo(SCREEN_ORDER.CONF_03), [navigateTo]);

  const goToDomainAssignment = useCallback(() => {
    if (tier === 'essentials') { navigateTo(SCREEN_ORDER.CONF_06); return; }
    navigateTo(SCREEN_ORDER.CONF_04);
  }, [navigateTo, tier]);

  const goToDomainCoverage = useCallback(() => {
    if (tier === 'essentials') { navigateTo(SCREEN_ORDER.CONF_06); return; }
    navigateTo(SCREEN_ORDER.CONF_05);
  }, [navigateTo, tier]);

  const goToGateSummary = useCallback(() => navigateTo(SCREEN_ORDER.CONF_06), [navigateTo]);

  const completeConfigure = useCallback(() => {
    window.dispatchEvent(
      new CustomEvent('decisionflow:navigate', {
        detail: { target: 'SETUP', source: 'CONFIGURE', tenantId, tier }
      })
    );
  }, [tenantId, tier]);

  const renderStep = () => {
    switch (currentScreen) {
      case SCREEN_ORDER.CONF_01:
        return <ReferenceDataReview tenantId={tenantId} tier={tier} importLogId={importLogId} onContinue={goToFiscalPeriods} />;
      case SCREEN_ORDER.CONF_02:
        return <FiscalPeriodActivation tenantId={tenantId} tier={tier} onBack={goToReferenceData} onContinue={goToCompAdmins} />;
      case SCREEN_ORDER.CONF_03:
        return <CompAdminUserCreation tenantId={tenantId} tier={tier} onBack={goToFiscalPeriods} onContinue={goToDomainAssignment} />;
      case SCREEN_ORDER.CONF_04:
        if (tier === 'essentials') {
          return <ConfigureGateSummary tenantId={tenantId} tier={tier} onBack={goToCompAdmins} onComplete={completeConfigure} />;
        }
        return <DomainAssignment tenantId={tenantId} tier={tier} onBack={goToCompAdmins} onContinue={goToDomainCoverage} />;
      case SCREEN_ORDER.CONF_05:
        if (tier === 'essentials') {
          return <ConfigureGateSummary tenantId={tenantId} tier={tier} onBack={goToCompAdmins} onComplete={completeConfigure} />;
        }
        return (
          <DomainCoverageSummary
            tenantId={tenantId} tier={tier}
            aiStatus={aiStatus} aiResponse={aiResponse}
            onAiStatusChange={setAiStatus} onAiResponseReceived={setAiResponse}
            onBack={goToDomainAssignment} onContinue={goToGateSummary}
          />
        );
      case SCREEN_ORDER.CONF_06:
      default:
        return (
          <ConfigureGateSummary
            tenantId={tenantId} tier={tier}
            onBack={tier === 'essentials' ? goToCompAdmins : goToDomainCoverage}
            onComplete={completeConfigure}
          />
        );
    }
  };

  return (
    <main className="min-h-screen bg-[rgb(var(--color-bg))]">
      <section className="mx-auto max-w-7xl px-6 py-8">
        <div className="mb-6 rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-5 shadow-sm">
          <p className="text-tok-sm font-medium uppercase tracking-wide text-[rgb(var(--color-text-muted))]">
            DecisionFlow Configure
          </p>
          <h1 className="mt-1 text-tok-2xl font-semibold text-[rgb(var(--color-text))]">
            Tenant administration setup
          </h1>
          <p className="mt-2 text-tok-sm text-[rgb(var(--color-text-muted))]">
            Review reference data, activate fiscal periods, invite comp admins,
            assign scope, and clear configure gates.
          </p>
        </div>

        {renderStep()}
      </section>
    </main>
  );
}
